import styled from 'styled-components';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';
import { FONTS } from '@r1-frontend/ui-react/tokens/fonts';

const emptyIndent = 'margin:0;';

type THeaderColor =
    | 'TextPrimary'
    | 'TextSecondary'
    | 'TextInfo'
    | 'TextHint'
    | 'TextDisabled'
    | 'TextWhite';

const getColor = ({ $color = 'TextPrimary' }: { $color?: THeaderColor }): string => {
    return COLORS[$color];
};

export type THStaticSize = {
    $desktop?: never,
    $mobile?: never,
} | {
    $desktop: boolean,
    $mobile?: never,
} | {
    $desktop?: never,
    $mobile: boolean,
}

export type THBaseProps = {
    $withIndent?: boolean,
    $inline?: boolean,
    $color?: THeaderColor,
} & THStaticSize;

export type THSmallerProps = {
    $smaller?: boolean,
} & THBaseProps;

/**
 * Desktop: 56/68
 * Mobile: 32/40
 */
export const H1 = styled.h1<THBaseProps>`
    width: ${({ $inline = false }) => $inline ? 'auto' : '100%'};
    ${({ $withIndent = true }) => $withIndent ? 'margin: 0 0 24px;' : emptyIndent}
    
    color: ${getColor};

    ${({ $desktop }) => $desktop ? FONTS.H0 : FONTS.H2};

    @media (min-width: ${wideBreakpoints.mobile}) {
        ${({ $withIndent = true }) => $withIndent ? 'margin-bottom: 56px;' : emptyIndent}
        ${({ $mobile }) => $mobile ? FONTS.H2 : FONTS.H0};
    }
`;

/**
 * Desktop: 40/52, $smaller: 32/40
 * Mobile: 24/32
 */
export const H2 = styled.h2<THSmallerProps>`
    width: ${({ $inline = false }) => $inline ? 'auto' : '100%'};
    ${({ $withIndent = true }) => $withIndent ? 'margin: 0 0 24px;' : emptyIndent}
    
    color: ${getColor};

    ${({ $desktop, $smaller }) => $desktop ? $smaller ? FONTS.H2 : FONTS.H1 : FONTS.H3};

    @media (min-width: ${wideBreakpoints.mobile}) {
        ${({ $mobile, $smaller = false }) => $mobile ? FONTS.H3 : $smaller ? FONTS.H2 : FONTS.H1};
    }
`;

/**
 * Desktop: 24/32
 * Mobile: 20/28
 */
export const H3 = styled.h3<THBaseProps>`
    width: ${({ $inline = false }) => $inline ? 'auto' : '100%'};
    ${({ $withIndent = true }) => $withIndent ? 'margin: 0 0 16px;' : emptyIndent}

    color: ${getColor};

    ${({ $desktop }) => $desktop ? FONTS.H3 : FONTS.H4};

    @media (min-width: ${wideBreakpoints.mobile}) {
        ${({ $mobile }) => $mobile ? FONTS.H4 : FONTS.H3};
        ${({ $withIndent = true }) => $withIndent ? 'margin: 0 0 24px;' : emptyIndent}
    }
`;

/**
 * Desktop: 20/28, $smaller: 16/24
 * Mobile: 16/24
 */
export const H4 = styled.h4<THSmallerProps>`
    width: ${({ $inline = false }) => $inline ? 'auto' : '100%'};
    ${({ $withIndent = true }) => $withIndent ? 'margin: 0 0 16px;' : emptyIndent}

    color: ${getColor};

    ${({ $desktop, $smaller }) => $desktop ? $smaller ? FONTS.H5 : FONTS.H4 : FONTS.H5};

    @media (min-width: ${wideBreakpoints.mobile}) {
        ${({ $mobile, $smaller = false }) => $smaller || $mobile ? FONTS.H5 : FONTS.H4};
    }
`;

/**
 * All: 14/20
 */
export const H5 = styled.h5<Omit<THBaseProps, '$desktop' | '$mobile'>>`
    width: ${({ $inline = false }) => $inline ? 'auto' : '100%'};
    ${({ $withIndent = true }) => $withIndent ? 'margin: 0 0 16px;' : emptyIndent}

    color: ${getColor};

    ${FONTS.H6};
`;

/**
 * @deprecated
 */
export const H6 = styled.h6<Omit<THBaseProps, '$desktop' | '$mobile'>>`
    width: ${({ $inline = false }) => $inline ? 'auto' : '100%'};

    color: ${getColor};

    ${FONTS.LABEL};

    text-transform: uppercase;
`;
